import { useState, useEffect } from 'react';
import { db } from '../firebase';
import { doc, getDoc, runTransaction } from 'firebase/firestore';
import dayjs from 'dayjs';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import isoWeek from 'dayjs/plugin/isoWeek';
import WeekView from './schedule/WeekView';
import DayView from './schedule/DayView';
import { useAuth } from '../contexts/AuthContext';

// Enable plugins for week calculations
dayjs.extend(weekOfYear);
dayjs.extend(isoWeek);

const Schedule = () => {
  // State for current week start (Sunday)
  const [weekStart, setWeekStart] = useState(() => {
    const today = dayjs();
    return today.startOf('week'); // Sunday as first day (isoWeek plugin)
  });
  // State for schedule data (month documents)
  const [scheduleData, setScheduleData] = useState({});
  // State for loading
  const [loading, setLoading] = useState(true);
  // State for view (week or day)
  const [view, setView] = useState('week');
  // State for selected day in edit view
  const [selectedDay, setSelectedDay] = useState(null);
  // State for error handling
  const [error, setError] = useState(null);

  const { currentUser } = useAuth();

  // Fetch schedule data for missing months
  const fetchSchedule = async (months) => {
    setLoading(true);
    try {
      const newData = { ...scheduleData };
      const missingMonths = months.filter((month) => !scheduleData[month]);

      for (const month of missingMonths) {
        const docRef = doc(db, 'schedule', month);
        const docSnap = await getDoc(docRef);
        newData[month] = docSnap.exists()
          ? docSnap.data()
          : { month: `${month.slice(0, 4)}-${month.slice(4)}`, days: {} };
      }

      setScheduleData(newData);
    } catch (error) {
      console.error('Error fetching schedule:', error);
      setError('Failed to fetch schedule: ' + error.message);
    }
    setLoading(false);
  };

  // Handle week navigation and fetch data if needed
  useEffect(() => {
    if (view === 'week') {
      // Determine months to fetch (current week and next week)
      const weekEnd = weekStart.endOf('week');
      const months = [
        weekStart.format('YYYYMM'),
        weekEnd.format('YYYYMM'),
      ].filter((v, i, a) => a.indexOf(v) === i); // Unique months

      // Fetch only if any month is missing
      if (months.some((month) => !scheduleData[month])) {
        fetchSchedule(months);
      } else {
        setLoading(false); // No fetch needed, data is cached
      }
    }
  }, [weekStart, scheduleData, view]);

  // Handle view switch to day edit
  const handleEditDay = (day) => {
    setSelectedDay(day);
    setView('day');
  };

  // Handle cancel (back to week view)
  const handleCancel = () => {
    setView('week');
    setSelectedDay(null);
    setError(null);
  };

  // Handle save changes
  const handleSave = async (updatedData) => {
    // Ensure only admins can save
    if (currentUser.role !== 'admin') {
      setError('Only admins can update the schedule.');
      return;
    }

    try {
      const monthKey = selectedDay.date.format('YYYYMM');
      const dayKey = selectedDay.date.format('DD');
      const scheduleRef = doc(db, 'schedule', monthKey);
      const practitionersRef = doc(db, 'app', 'practitioners');
      const currentDate = dayjs().format('YYYY-MM-DD');

      // Filter out practitioners with 0 scans
      const filteredData = {
        ...updatedData,
        practitioners: updatedData.practitioners.filter((p) => p.scans > 0),
      };

      // Use a transaction to update schedule
      await runTransaction(db, async (transaction) => {
        // Get current schedule and practitioners data
        const scheduleDoc = await transaction.get(scheduleRef);
        const practitionersDoc = await transaction.get(practitionersRef);

        // Prepare schedule update
        let days = scheduleDoc.exists() ? scheduleDoc.data().days || {} : {};

        days[dayKey] = filteredData;

        // Write updates
        transaction.set(scheduleRef, {
          month: `${monthKey.slice(0, 4)}-${monthKey.slice(4)}`,
          days,
        }, { merge: true });
      });

      // Update local state to reflect changes
      setScheduleData((prev) => ({
        ...prev,
        [monthKey]: {
          ...prev[monthKey],
          days: {
            ...prev[monthKey]?.days,
            [dayKey]: filteredData,
          },
        },
      }));
      setView('week');
      setSelectedDay(null);
      setError(null);
    } catch (error) {
      console.error('Error saving schedule:', error);
      setError('Failed to save schedule: ' + error.message);
    }
  };

  return (
    <>
      {view === 'week' ? (
        <WeekView
          weekStart={weekStart}
          setWeekStart={setWeekStart}
          scheduleData={scheduleData}
          loading={loading}
          onEditDay={handleEditDay}
        />
      ) : (
        <DayView
          selectedDay={selectedDay}
          scheduleData={scheduleData}
          onCancel={handleCancel}
          onSave={handleSave}
        />
      )}
    </>
  );
};

export default Schedule;